import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

export default function Progress() {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ period: new Date().toISOString().slice(0, 7), academic: 75, discipline: 80, sports: 70, social: 85, note: "" });

  useEffect(() => {
    api.get("/students").then((r) => {
      setStudents(r.data);
      if (r.data.length) setStudentId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!studentId) return;
    api.get(`/hostel/progress?student_id=${studentId}`).then((r) => setRows(r.data));
  }, [studentId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/hostel/progress", { ...form, student_id: studentId, academic: Number(form.academic), discipline: Number(form.discipline), sports: Number(form.sports), social: Number(form.social) });
      toast.success("Progress submitted — donor auto-notified");
      const r = await api.get(`/hostel/progress?student_id=${studentId}`);
      setRows(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const latest = rows[rows.length - 1];
  const radarData = latest ? [
    { dim: "Academic", v: latest.academic },
    { dim: "Discipline", v: latest.discipline },
    { dim: "Sports", v: latest.sports },
    { dim: "Social", v: latest.social },
  ] : [];

  return (
    <DashboardLayout title="Progress Tracking" subtitle="4-dimension monthly scoring. Donors notified on submission.">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <form onSubmit={submit} className="lg:col-span-1 vs-card p-5 space-y-3">
          <div>
            <Label>Student</Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger data-testid="progress-student-select"><SelectValue /></SelectTrigger>
              <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.vs_id} — {s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Period</Label><Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="2026-02" /></div>
          {[
            ["academic", "Academic"], ["discipline", "Discipline"],
            ["sports", "Sports"], ["social", "Social"]
          ].map(([k, l]) => (
            <div key={k}>
              <Label>{l} (0-100)</Label>
              <Input data-testid={`progress-${k}`} type="number" min={0} max={100} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </div>
          ))}
          <div><Label>Note</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          <Button data-testid="progress-submit" type="submit" className="w-full bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]">Submit progress</Button>
        </form>

        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="vs-card p-5">
            <div className="eyebrow">Latest report</div>
            <div className="font-display text-lg font-semibold mb-3">{latest?.period || "—"}</div>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="var(--vs-border)" />
                  <PolarAngleAxis dataKey="dim" tick={{ fontSize: 11, fill: "var(--vs-text-secondary)" }} />
                  <Radar dataKey="v" stroke="var(--vs-secondary)" fill="var(--vs-secondary)" fillOpacity={0.35} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="vs-card p-5">
            <div className="eyebrow">Trend</div>
            <div className="font-display text-lg font-semibold mb-3">All dimensions</div>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <LineChart data={rows}>
                  <CartesianGrid stroke="var(--vs-border)" strokeDasharray="2 4" />
                  <XAxis dataKey="period" tick={{ fontSize: 10, fill: "var(--vs-text-secondary)" }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "var(--vs-text-secondary)" }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="academic" stroke="var(--vs-primary)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="discipline" stroke="var(--vs-secondary)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="sports" stroke="var(--vs-info)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="social" stroke="var(--vs-warning)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
